import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { COLLECTIONS } from '../../constants/firebaseConfig';
import { CART_STATUS, StatusHelpers } from '../../constants';
import { resolvePersistedGctSummary } from '../../utils/pricing/gctTax';
import { createLogger } from '../../utils/logger';
import { parseWipayDataQueryParam } from './parseWipayCallback';
import {
  getAccessibleCartDocs,
  findCartInstanceInCartDocs,
  findLegacyCartInUsersByOrderId,
  isSupersededCartOrderId,
  resolveCheckoutSnapshotForOrder,
  shouldFinalizeSuccessForCart,
  shouldResetCartForFailedOrder,
  applySupersededOrderFulfillmentToCart,
  tryClaimCartCheckoutFulfillment,
  shouldRunInventoryDecrementForOrder,
  shouldSkipFulfillmentAfterFailedClaim,
} from './resolveCartInstance';
import {
  isEmployeeNotificationEnabled,
  notifyEmployeesNewOrder,
  resolveCustomerNameForNotification,
} from './notifyEmployees';
import {
  decrementInventory,
  restoreInventoryDecrements,
  tryClaimInventoryDecrement,
} from './paymentInventory';
import { shouldFulfillSupersededOrderPayment } from './checkoutOrderGuard';

const logger = createLogger('finalizeCartPayment');

const confirmationFee = parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500;
const paymentGateway = process.env.REACT_APP_PAYMENT_GATEWAY || 'WiPay';
const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'Credit Card';

async function fetchCustomerName(userId, fallback = 'Customer') {
  if (!userId || userId === 'unknown') return fallback;
  try {
    const usersQuery = query(
      collection(db, COLLECTIONS.USERS),
      where('identity_id', '==', userId),
      limit(1)
    );
    const userSnapshot = await getDocs(usersQuery);
    if (!userSnapshot.empty) {
      const userData = userSnapshot.docs[0].data();
      return userData.first_name || fallback;
    }
  } catch (error) {
    logger.error('[finalizeCartPayment] Error fetching customer name:', error);
  }
  return fallback;
}

async function loadWipayRecord(orderId) {
  const wipayRef = doc(db, COLLECTIONS.WIPAY_PAYMENTS, orderId);
  const wipaySnap = await getDoc(wipayRef);
  if (!wipaySnap.exists()) {
    return { wipayRef, wipaySnap: null, wipayData: null };
  }
  return { wipayRef, wipaySnap, wipayData: wipaySnap.data() };
}

async function resolveCheckoutContext(q, wipayData) {
  let checkoutData = parseWipayDataQueryParam(q.data || wipayData?.data);
  let cartInstanceData = null;
  let resolvedCartMatch = null;
  let resolvedSupermarketIdFromUsers = null;

  const needsCartLookup =
    !checkoutData.cartItems?.length ||
    !checkoutData.cartInstanceId ||
    (!checkoutData.supermarketId && !checkoutData.supermarket_id);

  if (needsCartLookup) {
    const cartDocs = await getAccessibleCartDocs({
      checkoutData,
      fallbackUserId: q.user_id || wipayData?.user_id,
    });
    const cartMatch = findCartInstanceInCartDocs(cartDocs, q.order_id, checkoutData);
    if (cartMatch) {
      resolvedCartMatch = cartMatch;
      cartInstanceData = cartMatch.cartInstance;
    }

    if (!cartInstanceData) {
      const legacyMatch = await findLegacyCartInUsersByOrderId(q.order_id, {
        checkoutData,
        fallbackUserId: q.user_id || wipayData?.user_id,
      });
      if (legacyMatch) {
        cartInstanceData = legacyMatch.cartInstanceData;
        resolvedSupermarketIdFromUsers = legacyMatch.supermarketId;
      }
    }
  }

  const persistedCheckoutSnapshot = resolveCheckoutSnapshotForOrder(cartInstanceData, q.order_id);
  if (persistedCheckoutSnapshot && typeof persistedCheckoutSnapshot === 'object') {
    checkoutData = { ...persistedCheckoutSnapshot, ...checkoutData };
  }

  return {
    checkoutData,
    cartInstanceData,
    resolvedCartMatch,
    resolvedSupermarketIdFromUsers,
  };
}

function buildTransactionPayload({
  q,
  checkoutData,
  cartInstanceData,
  resolvedCartMatch,
  resolvedSupermarketIdFromUsers,
  existingTransaction,
  forcedStatus,
}) {
  const userId = checkoutData.userId || cartInstanceData?.userId || q.user_id || 'unknown';
  const customerName =
    checkoutData.customerName || cartInstanceData?.customerName || q.customer_name || 'Customer';

  const resolvedSupermarketId =
    checkoutData.supermarketId ||
    checkoutData.supermarket_id ||
    cartInstanceData?.supermarketId ||
    resolvedCartMatch?.supermarketId ||
    resolvedSupermarketIdFromUsers ||
    q.supermarket_id ||
    'unknown';

  const resolvedCartInstanceId =
    checkoutData.cartInstanceId || resolvedCartMatch?.cartInstanceId || q.order_id;

  const persistedGctSummary = resolvePersistedGctSummary(checkoutData, cartInstanceData, q);

  let transactionStatus = forcedStatus || CART_STATUS.AWAITING_PACKING;
  if (!forcedStatus && existingTransaction) {
    if (StatusHelpers.isMoreAdvancedThanAwaitingPacking(existingTransaction.status)) {
      transactionStatus = existingTransaction.status;
    } else {
      transactionStatus = CART_STATUS.AWAITING_PACKING;
    }
  }

  const amountPaid =
    parseFloat(q.total) ||
    (checkoutData.paymentType === 'full'
      ? (checkoutData.orderSubtotal || 0) +
        (checkoutData.confirmationFee || confirmationFee) +
        (Number(checkoutData.deliveryFee) || 0)
      : (checkoutData.confirmationFee || confirmationFee) + (Number(checkoutData.deliveryFee) || 0));

  return {
    userId,
    cartDocId:
      resolvedCartMatch?.cartDoc?.id || checkoutData.cartDocId || q.user_id || null,
    supermarketId: resolvedSupermarketId,
    supermarketName:
      checkoutData.supermarketName ||
      cartInstanceData?.supermarketName ||
      q.supermarket_name ||
      'Unknown Supermarket',
    customerName,
    cartInstanceId: resolvedCartInstanceId,
    cartItems:
      checkoutData.cartItems ||
      cartInstanceData?.products ||
      (q.cart_items ? JSON.parse(q.cart_items) : []),
    confirmationFee: checkoutData.confirmationFee || confirmationFee,
    orderSubtotal:
      checkoutData.orderSubtotal || cartInstanceData?.orderSubtotal || parseFloat(q.order_subtotal) || 0,
    paymentType: checkoutData.paymentType || q.payment_type || 'confirmation_fee_only',
    deliveryOption:
      checkoutData.deliveryOption || cartInstanceData?.deliveryOption || q.delivery_option || 'pickup',
    deliveryAddress:
      checkoutData.deliveryAddress || cartInstanceData?.deliveryAddress || q.delivery_address || null,
    deliveryPhone:
      checkoutData.deliveryPhone || cartInstanceData?.deliveryPhone || q.delivery_phone || null,
    deliveryFee:
      Number(
        checkoutData.deliveryFee ?? checkoutData.delivery_fee ?? cartInstanceData?.deliveryFee
      ) || 0,
    maxDeliveryDistance: checkoutData.maxDeliveryDistance || null,
    taxInclusiveTotal:
      checkoutData.taxInclusiveTotal ||
      checkoutData.orderSubtotal ||
      cartInstanceData?.orderSubtotal ||
      parseFloat(q.order_subtotal) ||
      0,
    gctIncludedTotal:
      Number(
        checkoutData.gctIncludedTotal ??
          q.gct_included_total ??
          persistedGctSummary?.gctIncludedTotal ??
          persistedGctSummary?.totals?.gctIncluded
      ) || 0,
    taxSummary: persistedGctSummary,
    gctSummary: persistedGctSummary,
    status: transactionStatus,
    amountPaid: forcedStatus === CART_STATUS.PAYMENT_FAILED ? 0 : amountPaid,
    paymentGatewayTransactionId: q.transaction_id || `admin_${Date.now()}`,
    paymentGateway,
    paymentMethod: q.method || paymentMethod,
    message: q.message || (forcedStatus === CART_STATUS.PAYMENT_FAILED ? 'Payment marked failed by admin' : 'Payment resolved as successful by admin'),
    total: forcedStatus === CART_STATUS.PAYMENT_FAILED ? 0 : amountPaid,
    createdAt: existingTransaction?.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(existingTransaction?.inventoryDecremented === true ? { inventoryDecremented: true } : {}),
    adminResolved: true,
    adminResolvedAt: serverTimestamp(),
  };
}

async function updateCartAfterSuccess({
  orderId,
  checkoutData,
  transactionData,
  transactionStatus,
  notifyEmployees = true,
}) {
  if (transactionStatus !== CART_STATUS.AWAITING_PACKING) return;

  const cartDocs = await getAccessibleCartDocs({
    checkoutData,
    fallbackUserId: transactionData.userId,
  });
  const cartMatch = findCartInstanceInCartDocs(cartDocs, orderId, checkoutData);
  if (!cartMatch) {
    logger.warn('[finalizeCartPayment] Cart instance not found for order_id:', orderId);
    return;
  }

  const {
    cartDoc,
    cartData,
    supermarketCart,
    cartInstanceId: actualCartInstanceId,
    cartInstance: foundCartInstance,
  } = cartMatch;

  if (!shouldFinalizeSuccessForCart(foundCartInstance, orderId)) {
    logger.warn(
      '[finalizeCartPayment] Skipping cart update for superseded or unrelated order_id:',
      orderId
    );
    return;
  }

  let statusWasUpdated = false;
  if (StatusHelpers.canUpdateToAwaitingPacking(foundCartInstance.status)) {
    applySupersededOrderFulfillmentToCart(supermarketCart[actualCartInstanceId], orderId);
    supermarketCart[actualCartInstanceId].status = CART_STATUS.AWAITING_PACKING;
    supermarketCart[actualCartInstanceId].lastUpdatedAt = new Date();
    if (supermarketCart[actualCartInstanceId].pendingCheckoutSnapshots?.[orderId]) {
      delete supermarketCart[actualCartInstanceId].pendingCheckoutSnapshots[orderId];
    }
    await setDoc(cartDoc.ref, cartData, { merge: true });
    statusWasUpdated = true;
  }

  if (statusWasUpdated && notifyEmployees && isEmployeeNotificationEnabled()) {
    const customerName = await resolveCustomerNameForNotification(transactionData.userId);
    notifyEmployeesNewOrder({
      orderId: actualCartInstanceId,
      transactionId: orderId,
      customerName,
      supermarketId: transactionData.supermarketId,
      logContext: 'finalizeCartPayment',
    });
  }
}

async function resetCartAfterFailure({ orderId, checkoutData, fallbackUserId }) {
  const cartDocs = await getAccessibleCartDocs({ checkoutData, fallbackUserId });
  const cartMatch = findCartInstanceInCartDocs(cartDocs, orderId, checkoutData);
  if (!cartMatch) {
    logger.warn('[finalizeCartPayment] Cart instance not found to reset for order_id:', orderId);
    return;
  }

  const { cartDoc, cartData, supermarketCart, cartInstanceId, cartInstance } = cartMatch;

  if (isSupersededCartOrderId(cartInstance, orderId)) {
    if (supermarketCart[cartInstanceId].pendingCheckoutSnapshots?.[orderId]) {
      delete supermarketCart[cartInstanceId].pendingCheckoutSnapshots[orderId];
      if (Object.keys(supermarketCart[cartInstanceId].pendingCheckoutSnapshots).length === 0) {
        delete supermarketCart[cartInstanceId].pendingCheckoutSnapshots;
      }
      supermarketCart[cartInstanceId].lastUpdatedAt = new Date();
      await setDoc(cartDoc.ref, cartData, { merge: true });
    }
    logger.warn(
      '[finalizeCartPayment] Pruned superseded pending snapshot only for order_id:',
      orderId
    );
    return;
  }

  if (!shouldResetCartForFailedOrder(cartInstance, orderId)) {
    logger.warn(
      '[finalizeCartPayment] Skipping cart reset for unrelated order_id:',
      orderId
    );
    return;
  }

  supermarketCart[cartInstanceId].status = CART_STATUS.ACTIVE;
  supermarketCart[cartInstanceId].orderid = null;
  delete supermarketCart[cartInstanceId].checkoutSnapshot;
  supermarketCart[cartInstanceId].lastUpdatedAt = new Date();
  await setDoc(cartDoc.ref, cartData, { merge: true });
}

/**
 * Persist an unprocessed payment as successful (creates/updates transaction, cart, inventory).
 * @param {string} orderId
 * @param {{ resolvedBy?: string, notifyEmployees?: boolean }} [options]
 */
export async function finalizeCartPaymentAsSuccess(orderId, options = {}) {
  if (!orderId) {
    throw new Error('Order ID is required.');
  }

  const { wipayRef, wipayData } = await loadWipayRecord(orderId);
  const q = {
    ...(wipayData || {}),
    order_id: orderId,
    status: 'success',
  };

  let { checkoutData, cartInstanceData, resolvedCartMatch, resolvedSupermarketIdFromUsers } =
    await resolveCheckoutContext(q, wipayData);

  if (!cartInstanceData && (checkoutData.cartInstanceId || orderId)) {
    const cartDocs = await getAccessibleCartDocs({
      checkoutData,
      fallbackUserId: q.user_id || wipayData?.user_id,
    });
    const cartMatch = findCartInstanceInCartDocs(cartDocs, orderId, checkoutData);
    if (cartMatch) {
      resolvedCartMatch = cartMatch;
      cartInstanceData = cartMatch.cartInstance;
    }
  }

  if (
    cartInstanceData &&
    !shouldFulfillSupersededOrderPayment(cartInstanceData, orderId)
  ) {
    throw new Error(
      'This cart instance was already fulfilled by a newer checkout order. Resolve the current order instead.'
    );
  }

  const eligibleForFreshFulfillment =
    cartInstanceData && shouldFinalizeSuccessForCart(cartInstanceData, orderId);

  let claimedCartFulfillment = false;
  let cartClaimReason = null;
  if (
    eligibleForFreshFulfillment &&
    resolvedCartMatch?.cartDoc?.ref &&
    resolvedCartMatch.supermarketId &&
    resolvedCartMatch.cartInstanceId
  ) {
    const claimResult = await tryClaimCartCheckoutFulfillment({
      cartDocRef: resolvedCartMatch.cartDoc.ref,
      supermarketId: resolvedCartMatch.supermarketId,
      cartInstanceId: resolvedCartMatch.cartInstanceId,
      orderId,
    });
    claimedCartFulfillment = claimResult.claimed;
    cartClaimReason = claimResult.reason;
    if (
      !claimedCartFulfillment &&
      shouldSkipFulfillmentAfterFailedClaim(cartInstanceData, orderId, claimResult.reason)
    ) {
      throw new Error(
        'This cart instance was already fulfilled by another payment callback.'
      );
    }
  }

  const transactionRef = doc(db, COLLECTIONS.TRANSACTIONS, orderId);
  const existingTransactionSnap = await getDoc(transactionRef);
  const existingTransaction = existingTransactionSnap.exists()
    ? existingTransactionSnap.data()
    : null;

  const transactionData = buildTransactionPayload({
    q,
    checkoutData,
    cartInstanceData,
    resolvedCartMatch,
    resolvedSupermarketIdFromUsers,
    existingTransaction,
  });

  transactionData.customerName = await fetchCustomerName(
    transactionData.userId,
    transactionData.customerName
  );
  transactionData.adminResolvedBy = options.resolvedBy || null;
  transactionData.adminResolvedAs = 'success';

  await setDoc(
    wipayRef,
    {
      ...(wipayData || {}),
      order_id: orderId,
      status: 'success',
      admin_resolved: true,
      admin_resolved_as: 'success',
      admin_resolved_at: serverTimestamp(),
      admin_resolved_by: options.resolvedBy || null,
      message: q.message || 'Payment resolved as successful by admin',
    },
    { merge: true }
  );

  await setDoc(transactionRef, transactionData, { merge: true });

  const inventoryAlreadyProcessed =
    existingTransaction?.inventoryDecremented === true ||
    StatusHelpers.isMoreAdvancedThanAwaitingPacking(existingTransaction?.status);

  const shouldRunInventoryDecrement = shouldRunInventoryDecrementForOrder({
    cartInstanceData,
    orderId,
    eligibleForFreshFulfillment,
    claimedCartFulfillment,
    hasResolvedCartDoc: Boolean(resolvedCartMatch?.cartDoc?.ref),
    claimReason: cartClaimReason,
  });
  const skipInventoryForSupersededCart = !shouldRunInventoryDecrement;

  if (skipInventoryForSupersededCart) {
    logger.warn(
      '[finalizeCartPayment] Skipping inventory decrement for superseded or unrelated order_id:',
      orderId
    );
  }

  if (
    !skipInventoryForSupersededCart &&
    !inventoryAlreadyProcessed &&
    transactionData.supermarketId &&
    transactionData.supermarketId !== 'unknown' &&
    Array.isArray(transactionData.cartItems) &&
    transactionData.cartItems.length > 0
  ) {
    const claimedDecrement = await tryClaimInventoryDecrement(transactionRef);
    if (claimedDecrement) {
      const { allSucceeded, decrementedItems } = await decrementInventory(
        transactionData.supermarketId,
        transactionData.cartItems
      );
      if (!allSucceeded) {
        if (decrementedItems.length > 0) {
          await restoreInventoryDecrements(transactionData.supermarketId, decrementedItems);
        }
        await updateDoc(transactionRef, {
          inventoryDecremented: false,
          updatedAt: serverTimestamp(),
        });
      }
    }
  }

  await updateCartAfterSuccess({
    orderId,
    checkoutData,
    transactionData,
    transactionStatus: transactionData.status,
    notifyEmployees: options.notifyEmployees !== false,
  });

  return { orderId, status: transactionData.status, transactionData };
}

/**
 * Persist an unprocessed payment as failed (writes failed transaction and resets cart).
 * @param {string} orderId
 * @param {{ resolvedBy?: string }} [options]
 */
export async function finalizeCartPaymentAsFailed(orderId, options = {}) {
  if (!orderId) {
    throw new Error('Order ID is required.');
  }

  const { wipayRef, wipayData } = await loadWipayRecord(orderId);
  const q = {
    ...(wipayData || {}),
    order_id: orderId,
    status: 'failed',
  };

  const { checkoutData, cartInstanceData, resolvedCartMatch, resolvedSupermarketIdFromUsers } =
    await resolveCheckoutContext(q, wipayData);

  const transactionRef = doc(db, COLLECTIONS.TRANSACTIONS, orderId);
  const existingTransactionSnap = await getDoc(transactionRef);
  const existingTransaction = existingTransactionSnap.exists()
    ? existingTransactionSnap.data()
    : null;

  const transactionData = buildTransactionPayload({
    q,
    checkoutData,
    cartInstanceData,
    resolvedCartMatch,
    resolvedSupermarketIdFromUsers,
    existingTransaction,
    forcedStatus: CART_STATUS.PAYMENT_FAILED,
  });

  transactionData.customerName = await fetchCustomerName(
    transactionData.userId,
    transactionData.customerName
  );
  transactionData.adminResolvedBy = options.resolvedBy || null;
  transactionData.adminResolvedAs = 'failed';

  await setDoc(
    wipayRef,
    {
      ...(wipayData || {}),
      order_id: orderId,
      status: 'failed',
      admin_resolved: true,
      admin_resolved_as: 'failed',
      admin_resolved_at: serverTimestamp(),
      admin_resolved_by: options.resolvedBy || null,
      message: q.message || 'Payment marked failed by admin',
    },
    { merge: true }
  );

  await setDoc(transactionRef, transactionData, { merge: true });

  await resetCartAfterFailure({
    orderId,
    checkoutData,
    fallbackUserId: transactionData.userId,
  });

  return { orderId, status: CART_STATUS.PAYMENT_FAILED, transactionData };
}
